import {
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy,
  inject,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { PackageCardsComponent } from '../components/package-cards/package-cards.component';
import { PackageFiltersComponent } from '../components/package-filters/package-filters.component';
import { PackageTableComponent } from '../components/package-table/package-table.component';
import { AddPackageModalComponent } from '../components/add-package-modal/add-package-modal.component';
import {
  AddPrepaidModalComponent,
  PrepaidPackageFormData,
} from '../components/add-prepaid-modal/add-prepaid-modal.component';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PackageGridComponent } from '../components/package-grid/package-grid.component';
import { PackageService } from '../services/package.service';
import { AuthService } from '../services/auth.service';
import { AmplitudeService } from '../services/amplitude.service';
import { DeletePackageModalComponent } from '../components/delete-package-modal/delete-package-modal.component';
import { of, Subject } from 'rxjs';
import { take, filter, takeUntil, catchError } from 'rxjs/operators';
import { AppConfigService } from '../services/app-config.service';
import { FAMILY_PLAN_FEATURE, isFeatureOn } from '../shared/utils/app-feature.util';
import { NotificationService } from '../services/notification.service'; // NotificationService;
import { LoggerService } from '../services/logger.service';
import { Package, FilterOptions, SortOption } from '../shared/types';
import {
  FilterModalComponent,
  FilterCategory,
  FilterModalCustomSortAction,
  FilterOption,
} from '../shared/components/filter-modal/filter-modal.component';
import { ModalFooterActionsComponent } from '../shared/components/modal-footer-actions/modal-footer-actions.component';
import { MostUsedServicesModalComponent } from '../sales/components/most-used-services-modal/most-used-services-modal.component';
import {
  LoadingTableShimmerComponent,
  ShimmerColumn,
} from '../shared/components/loading-table-shimmer/loading-table-shimmer.component';
import {
  LocationSortOrderListItem,
  LocationSortOrderType,
  LocationSortOrderPreset,
  SortOrderService,
  parseSortOrderIdFromCreateResponse,
} from '../services/sort-order.service';
import { ProfileService } from '../services/profile.service';
import { LocationService } from '../services/location.service';
import { DialogMutexService } from '../services/dialog-mutex.service';
import { TabViewComponent, TabItem } from '../components/tab-view/tab-view.component';
import { PrepaidWashBooksComponent } from '../components/prepaid-wash-books/prepaid-wash-books.component';
import { mapPrepaidWashBookToFormData, PrepaidWashBook } from '../models/prepaid-wash-book.models';
import { EmptyStateComponent } from '../shared/components/empty-state';
import { buildPackageFilterParams } from '../shared/utils/package-filter-params.util';

@Component({
  selector: 'app-package',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    PackageCardsComponent,
    PackageFiltersComponent,
    PackageTableComponent,
    AddPackageModalComponent,
    AddPrepaidModalComponent,
    PackageGridComponent,
    DeletePackageModalComponent,
    EmptyStateComponent,
    FilterModalComponent,
    ModalFooterActionsComponent,
    MostUsedServicesModalComponent,
    LoadingTableShimmerComponent,
    TabViewComponent,
    PrepaidWashBooksComponent,
  ],
  templateUrl: './package.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./package.component.scss'],
})
export class PackageComponent implements OnInit, OnDestroy {
  @ViewChild(PackageTableComponent) packageTableComponent!: PackageTableComponent;
  @ViewChild('prepaidWashBooks') prepaidWashBooksComponent?: PrepaidWashBooksComponent;

  showModal = false;
  showPackageTypeModal = false;
  showPrepaidModal = false;
  prepaidEditBookId: string | null = null;
  prepaidEditData: PrepaidPackageFormData | null = null;
  selectedPackageKind: 'package' | 'prepaid_plan' = 'package';
  showDeleteModal = false;
  activePageTab = 'Packages';
  packagePageTabs: TabItem[] = [{ name: 'Packages' }, { name: 'Prepaid Wash Books' }];
  currentView = 'list';
  allPackages: Package[] = []; // Store packages from API (filtered or unfiltered)
  packages: Package[] = []; // Filtered packages to display
  selectedPackage: Package | null = null;
  packageToDelete: Package | null = null;
  packageImage = 'assets/package-3d/p-1.png';
  packageImage2 = 'assets/package-3d/p-2.png';
  packageImage3 = 'assets/package-3d/p-3.png';
  private destroy$ = new Subject<void>();
  private currentSearchTerm = '';
  private currentFilters: FilterOptions | null = null;
  private hasLoadedInitialPackages = false; // Track if we've loaded packages before
  private originalPackageCount = 0; // Track original unfiltered package count
  private isTableFiltering = false; // Track if table component is applying filters
  private hasTrackedPackageView = false; // Track if we've already tracked package view

  readonly packageListShimmerColumns: ShimmerColumn[] = [
    { label: 'Package', width: 'lg' },
    { label: 'Type', width: 'sm' },
    { label: 'Status', width: 'sm' },
    { label: 'Location', width: 'md' },
  ];

  /** Sort + “most used” panel (admin Packages page only; not routed to /sales) */
  isConfigureFilterModalVisible = false;
  configureFilterCategories: FilterCategory[] = [];
  private configureCurrentFilters: { carWashTypes: string[]; sortBy: string } = {
    carWashTypes: [],
    sortBy: '',
  };
  showMostUsedServicesModal = false;
  mostUsedModalMode: 'drag' | 'default-select' = 'drag';
  mostUsedLocationId: string | null = null;
  mostUsedSortOrderId: string | null = null;
  private configureLocationSortOrders: LocationSortOrderListItem[] = [];
  isConfigureSortOptionsLoading = false;
  /** Sort modal: locations for dropdown; selected id drives all sort / most-used APIs in this flow. */
  configureSalesLocations: { id: string; label: string }[] = [];
  configureSalesLocationId: string | null = null;

  /** Alias for template (`packageService` is constructor-injected; avoid field-init order issues). */
  get packagesLoading$() {
    return this.packageService.packagesLoading$;
  }

  private readonly appConfigService = inject(AppConfigService);

  /** CWS-1582: Family Plan section of Configure Sales Panel needs the family_plan flag on. */
  familyPlanFeatureOn = false;

  constructor(
    public packageService: PackageService,
    private authService: AuthService,
    private amplitudeService: AmplitudeService,
    private messageService: NotificationService,
    private logger: LoggerService,
    private route: ActivatedRoute,
    private router: Router,
    private sortOrderService: SortOrderService,
    private profileService: ProfileService,
    private locationService: LocationService,
    private dialogMutex: DialogMutexService
  ) {}

  /** Fails closed: until the config loads, or if it cannot, the Family Plan section stays hidden. */
  private loadFamilyPlanFeature(): void {
    this.appConfigService
      .getAppConfig()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: config => {
          this.familyPlanFeatureOn = isFeatureOn(config, FAMILY_PLAN_FEATURE);
        },
        error: () => {
          this.familyPlanFeatureOn = false;
        },
      });
  }

  // Getter for total packages count - use filtered packages
  get totalPackagesCount(): string {
    return this.packages.length.toString();
  }

  // Getter for active packages count - use filtered packages
  get activePackagesCount(): string {
    return this.packages.filter(pkg => pkg.package_status === true).length.toString();
  }

  // Getter for inactive packages count - use filtered packages
  get inactivePackagesCount(): string {
    return this.packages.filter(pkg => pkg.package_status === false).length.toString();
  }

  /** Open Sort + “most used” on the Packages page (keeps config out of the staff /sales area). */
  openConfigureSalesPanel(): void {
    this.syncConfigureSalesLocationsFromProfile();
    this.pickInitialConfigureSalesLocationId();
    this.initializeConfigureFilterCategories();
    this.isConfigureFilterModalVisible = true;
    this.isConfigureSortOptionsLoading = !!this.configureSalesLocationId;
    if (this.configureSalesLocationId) {
      this.prefillConfigureSortFromLocationPreference();
    } else {
      this.configureLocationSortOrders = [];
      this.initializeConfigureFilterCategories();
      this.isConfigureSortOptionsLoading = false;
    }
    this.profileService.loadLocations();
    this.profileService.locations$
      .pipe(
        filter(l => l.length > 0),
        take(1),
        takeUntil(this.destroy$)
      )
      .subscribe(locs => {
        if (!this.isConfigureFilterModalVisible || this.configureSalesLocations.length > 0) {
          return;
        }
        this.configureSalesLocations = this.mapProfileLocationsForSortPanel(locs);
        this.pickInitialConfigureSalesLocationId();
        if (this.configureSalesLocationId) {
          this.isConfigureSortOptionsLoading = true;
          this.prefillConfigureSortFromLocationPreference();
        }
      });
  }

  onConfigureSalesLocationChange(locationId: string | null): void {
    this.configureSalesLocationId = locationId;
    if (!locationId) {
      this.locationService.clearShopLocation();
      this.configureLocationSortOrders = [];
      this.configureCurrentFilters = { carWashTypes: [], sortBy: '' };
      this.initializeConfigureFilterCategories();
      this.isConfigureSortOptionsLoading = false;
      return;
    }
    if (!this.isConfigureFilterModalVisible) {
      return;
    }
    this.isConfigureSortOptionsLoading = true;
    this.reloadConfigureSortOrdersForLocation(String(locationId), true);
  }

  private syncConfigureSalesLocationsFromProfile(): void {
    const locs = this.profileService.getCurrentLocations();
    this.configureSalesLocations = locs?.length ? this.mapProfileLocationsForSortPanel(locs) : [];
  }

  private mapProfileLocationsForSortPanel(locs: any[]): { id: string; label: string }[] {
    return (locs || [])
      .map((l: any) => {
        const id = String(l?.id ?? '').trim();
        const labelRaw =
          l?.branch ?? l?.name ?? l?.location_name ?? l?.shop_name ?? l?.title ?? l?.label;
        const label =
          String(labelRaw ?? '').trim() || (id ? `Location ${id.slice(0, 8)}…` : 'Unnamed');
        return { id, label };
      })
      .filter(x => x.id.length > 0);
  }

  private pickInitialConfigureSalesLocationId(): void {
    if (this.configureSalesLocations.length === 0) {
      this.configureSalesLocationId = null;
      return;
    }
    const firstId = this.configureSalesLocations[0].id;
    const current = this.configureSalesLocationId?.trim();
    if (current && this.configureSalesLocations.some(l => l.id === current)) {
      return;
    }
    this.configureSalesLocationId = firstId;
  }

  private resolveConfigureSalesLocationId(): string | null {
    const id = this.configureSalesLocationId?.trim();
    return id || null;
  }

  private initializeConfigureFilterCategories(): void {
    const sortValue = this.configureCurrentFilters.sortBy;
    const customSortOptions: FilterOption[] = this.configureLocationSortOrders.map((row, index) => {
      const customId = String(row.id || '').trim();
      const customName = String(row.name || '').trim() || `Custom ${index + 1}`;
      return {
        id: `custom-order-${customId || index}`,
        label: customName,
        value: customId ? `custom-order:${customId}` : `custom-order:idx:${index}`,
        selected: customId ? sortValue === `custom-order:${customId}` : false,
      };
    });
    const sortOptions: FilterOption[] = [
      {
        id: 'price-high-low',
        label: 'Price High to Low',
        value: 'price-high-low',
        selected: sortValue === 'price-high-low',
      },
      {
        id: 'price-low-high',
        label: 'Price Low to High',
        value: 'price-low-high',
        selected: sortValue === 'price-low-high',
      },
      {
        id: 'ascending-order',
        label: 'Ascending Order',
        value: 'ascending-order',
        selected: sortValue === 'ascending-order',
      },
      {
        id: 'descending-order',
        label: 'Descending Order',
        value: 'descending-order',
        selected: sortValue === 'descending-order',
      },
      {
        id: 'custom-drag-drop',
        label: 'Custom(Drag & Drop)',
        value: 'custom-drag-drop',
        selected: sortValue === 'custom-drag-drop',
      },
      ...customSortOptions,
    ];
    this.configureFilterCategories = [
      { id: 'sortBy', name: 'Sort By', type: 'radio', options: sortOptions },
    ];
  }

  closeConfigureFilterModal(): void {
    this.isConfigureFilterModalVisible = false;
    this.isConfigureSortOptionsLoading = false;
  }

  onConfigureFiltersApplied(categories: FilterCategory[]): void {
    const sortCategory = categories.find(c => c.id === 'sortBy');
    this.configureCurrentFilters = {
      carWashTypes: [],
      sortBy: sortCategory?.options.find(o => o.selected)?.value || '',
    };
    if (this.configureCurrentFilters.sortBy === 'custom-drag-drop') {
      this.onConfigureMostUsedFromFilter();
      return;
    }

    const customSortId = this.getConfigureCustomSortId(this.configureCurrentFilters.sortBy);
    if (customSortId) {
      this.applyConfigureCustomSortOrder(customSortId);
      return;
    }

    const sortPreset = this.getConfigureLocationSortPreset(this.configureCurrentFilters.sortBy);
    if (!sortPreset) {
      this.closeConfigureFilterModal();
      return;
    }
    this.applyConfigureLocationSortOrder(sortPreset);
  }

  onConfigureFiltersCleared(): void {
    const locationId = this.resolveConfigureSalesLocationId();
    if (!locationId) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Location required',
        detail: 'Select a location before clearing sort filters.',
      });
      this.closeConfigureFilterModal();
      return;
    }
    this.sortOrderService
      .clearLocationSortOrder(locationId)
      .pipe(
        takeUntil(this.destroy$),
        catchError(err => {
          this.messageService.add({
            severity: 'error',
            summary: 'Clear failed',
            detail: err.error?.message || 'Could not clear sort preference for this location.',
          });
          return of(null);
        })
      )
      .subscribe(res => {
        if (res == null) {
          return;
        }
        if (res.status === false) {
          this.messageService.add({
            severity: 'error',
            summary: 'Clear failed',
            detail: res.message || 'The server did not clear the sort preference.',
          });
          return;
        }
        this.configureCurrentFilters = { carWashTypes: [], sortBy: '' };
        this.packageService.clearCarWashPackagesCache();
        if (this.currentFilters) {
          this.packageService.loadPackages(this.buildFilterParams(this.currentFilters));
        } else {
          this.packageService.loadPackages();
        }
        this.applyClientSideFilters();
        this.closeConfigureFilterModal();
        this.messageService.add({
          severity: 'success',
          summary: 'Cleared',
          detail: 'Sort preference was cleared for this location.',
        });
      });
  }

  onConfigureCustomSortAction(event: FilterModalCustomSortAction): void {
    if (!event.sortOrderId) {
      return;
    }
    const run = (locationId: string) => {
      if (event.action === 'rename') {
        const nextName = (event.newLabel ?? '').trim();
        if (!nextName) {
          this.messageService.add({
            severity: 'warn',
            summary: 'Invalid name',
            detail: 'Please enter a valid custom sort name.',
          });
          return;
        }
        this.sortOrderService
          .renameSortOrder(event.sortOrderId, nextName)
          .pipe(
            takeUntil(this.destroy$),
            catchError(err => {
              this.messageService.add({
                severity: 'error',
                summary: 'Rename failed',
                detail: err.error?.message || 'Could not rename custom sort.',
              });
              return of(null);
            })
          )
          .subscribe(res => {
            if (!res || res.status === false) {
              return;
            }
            this.reloadConfigureSortOrdersForLocation(locationId);
            this.messageService.add({
              severity: 'success',
              summary: 'Renamed',
              detail: 'Custom sort name updated.',
            });
          });
        return;
      }

      this.sortOrderService
        .deleteSortOrder(event.sortOrderId)
        .pipe(
          takeUntil(this.destroy$),
          catchError(err => {
            this.messageService.add({
              severity: 'error',
              summary: 'Delete failed',
              detail: err.error?.message || 'Could not delete custom sort.',
            });
            return of(null);
          })
        )
        .subscribe(res => {
          if (!res || res.status === false) {
            return;
          }
          // After custom delete, default back to first sort option (Price High to Low) and persist it.
          this.configureCurrentFilters = { carWashTypes: [], sortBy: 'price-high-low' };
          this.sortOrderService
            .updateLocationSortOrder(locationId, 'price_desc')
            .pipe(
              takeUntil(this.destroy$),
              catchError(() => of(null))
            )
            .subscribe(() => {
              this.reloadConfigureSortOrdersForLocation(locationId);
              this.messageService.add({
                severity: 'success',
                summary: 'Deleted',
                detail: 'Custom sort removed. Price High to Low is now selected by default.',
              });
            });
        });
    };

    const locationId = this.resolveConfigureSalesLocationId();
    if (!locationId) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Location required',
        detail: 'Select a location first.',
      });
      return;
    }
    run(locationId);
  }

  onConfigureMostUsedFromFilter(): void {
    this.mostUsedModalMode = 'drag';
    const startCreate = (locationId: string) => {
      this.sortOrderService
        .createForLocation(locationId)
        .pipe(
          takeUntil(this.destroy$),
          catchError(err => {
            this.messageService.add({
              severity: 'error',
              summary: 'Could not open',
              detail: err.error?.message || 'Failed to create sort order for this location.',
            });
            return of(null);
          })
        )
        .subscribe(res => {
          if (!res) {
            return;
          }
          const sortId = parseSortOrderIdFromCreateResponse(res.data);
          if (!sortId) {
            this.messageService.add({
              severity: 'error',
              summary: 'Invalid response',
              detail:
                'Sort order was created but the app could not read its id. Check the API response shape.',
            });
            return;
          }
          this.mostUsedLocationId = locationId;
          this.mostUsedSortOrderId = sortId;
          this.showMostUsedServicesModal = true;
          this.closeConfigureFilterModal();
        });
    };
    const locationId = this.resolveConfigureSalesLocationId();
    if (!locationId) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Location required',
        detail: 'Select a location first.',
      });
      return;
    }
    startCreate(locationId);
  }

  onConfigureMostUsedDefaults(): void {
    this.mostUsedModalMode = 'default-select';
    const openDefaults = (locationId: string) => {
      this.mostUsedLocationId = locationId;
      this.mostUsedSortOrderId = null;
      this.showMostUsedServicesModal = true;
      this.closeConfigureFilterModal();
    };

    const locationId = this.resolveConfigureSalesLocationId();
    if (!locationId) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Location required',
        detail: 'Select a location first.',
      });
      return;
    }
    openDefaults(locationId);
  }

  onCloseMostUsedServicesModal(): void {
    this.showMostUsedServicesModal = false;
    this.mostUsedModalMode = 'drag';
    this.mostUsedSortOrderId = null;
    this.mostUsedLocationId = null;
  }

  onMostUsedServicesSaved(): void {
    this.packageService.clearCarWashPackagesCache();
    if (this.currentFilters) {
      this.packageService.loadPackages(this.buildFilterParams(this.currentFilters));
    } else {
      this.packageService.loadPackages();
    }
  }

  private sortPackagesBySalesOptions(packages: Package[], sortBy: string): Package[] {
    const sorted = [...packages];
    switch (sortBy) {
      case 'price-high-low':
        return sorted.sort(
          (a, b) => parseFloat(b.single_list_price || '0') - parseFloat(a.single_list_price || '0')
        );
      case 'price-low-high':
        return sorted.sort(
          (a, b) => parseFloat(a.single_list_price || '0') - parseFloat(b.single_list_price || '0')
        );
      case 'ascending-order':
        return sorted.sort((a, b) => (a.package_name || '').localeCompare(b.package_name || ''));
      case 'descending-order':
        return sorted.sort((a, b) => (b.package_name || '').localeCompare(a.package_name || ''));
      default:
        return sorted;
    }
  }

  /** Maps Sort modal radio values to PUT `sort_order` body (see sort-order API). */
  private getConfigureLocationSortPreset(sortBy: string): LocationSortOrderPreset | null {
    switch (sortBy) {
      case 'price-high-low':
        return 'price_desc';
      case 'price-low-high':
        return 'price_asc';
      case 'ascending-order':
        return 'name_asc';
      case 'descending-order':
        return 'name_desc';
      default:
        return null;
    }
  }

  private getConfigureCustomSortId(sortBy: string): string | null {
    if (!sortBy.startsWith('custom-order:')) {
      return null;
    }
    const id = sortBy.slice('custom-order:'.length).trim();
    if (!id || id.startsWith('idx:')) {
      return null;
    }
    return id;
  }

  /** Reverse-map API preset to Sort modal option id. */
  private getConfigureSortByFromPreset(
    preset: LocationSortOrderType | null | undefined,
    customId: string | null | undefined = null
  ): string {
    switch (preset) {
      case 'price_desc':
        return 'price-high-low';
      case 'price_asc':
        return 'price-low-high';
      case 'name_asc':
        return 'ascending-order';
      case 'name_desc':
        return 'descending-order';
      default: {
        if (!customId) {
          return '';
        }
        const exists = this.configureLocationSortOrders.some(
          row => String(row.id || '').trim() === customId
        );
        return exists ? `custom-order:${customId}` : 'custom-drag-drop';
      }
    }
  }

  /** On panel open, fetch backend preference and pre-select the matching sort option. */
  private prefillConfigureSortFromLocationPreference(): void {
    const locationId = this.resolveConfigureSalesLocationId();
    if (!locationId) {
      this.isConfigureSortOptionsLoading = false;
      return;
    }
    this.reloadConfigureSortOrdersForLocation(locationId, true);
  }

  private reloadConfigureSortOrdersForLocation(
    locationId: string,
    includePreference = false
  ): void {
    this.sortOrderService
      .getLocationSortOrders(locationId)
      .pipe(
        takeUntil(this.destroy$),
        catchError(() => of({ status: false, data: [] as LocationSortOrderListItem[] }))
      )
      .subscribe(listRes => {
        this.configureLocationSortOrders =
          listRes.status && Array.isArray(listRes.data) ? listRes.data : [];
        if (!includePreference) {
          this.initializeConfigureFilterCategories();
          this.isConfigureSortOptionsLoading = false;
          return;
        }
        this.sortOrderService
          .getLocationSortPreference(locationId)
          .pipe(
            takeUntil(this.destroy$),
            catchError(() => of(null))
          )
          .subscribe(prefRes => {
            const pref = prefRes && prefRes.status !== false ? prefRes.data : null;
            const mappedSortBy = this.getConfigureSortByFromPreset(
              pref?.sort_order_type ?? null,
              pref?.sort_order_custom_id ?? null
            );
            this.configureCurrentFilters = {
              carWashTypes: [],
              sortBy: mappedSortBy || '',
            };
            this.initializeConfigureFilterCategories();
            this.isConfigureSortOptionsLoading = false;
          });
      });
  }

  private applyConfigureCustomSortOrder(customId: string): void {
    const run = (locationId: string) => {
      this.sortOrderService
        .updateLocationCustomSortOrder(locationId, customId)
        .pipe(
          takeUntil(this.destroy$),
          catchError(err => {
            this.messageService.add({
              severity: 'error',
              summary: 'Sort not saved',
              detail: err.error?.message || 'Failed to apply selected custom sort order.',
            });
            return of(null);
          })
        )
        .subscribe(res => {
          if (!res || res.status === false) {
            if (res?.status === false) {
              this.messageService.add({
                severity: 'error',
                summary: 'Sort not saved',
                detail: res.message || 'The server did not accept this custom sort order.',
              });
            }
            return;
          }
          this.packageService.clearCarWashPackagesCache();
          if (this.currentFilters) {
            this.packageService.loadPackages(this.buildFilterParams(this.currentFilters));
          } else {
            this.packageService.loadPackages();
          }
          this.applyClientSideFilters();
          this.closeConfigureFilterModal();
          this.messageService.add({
            severity: 'success',
            summary: 'Sort updated',
            detail: 'Custom sales sort order was applied for this location.',
          });
        });
    };

    const locationId = this.resolveConfigureSalesLocationId();
    if (!locationId) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Location required',
        detail: 'Select a location first.',
      });
      return;
    }
    run(locationId);
  }

  private applyConfigureLocationSortOrder(sortOrder: LocationSortOrderPreset): void {
    const run = (locationId: string) => {
      this.sortOrderService
        .updateLocationSortOrder(locationId, sortOrder)
        .pipe(
          takeUntil(this.destroy$),
          catchError(err => {
            this.messageService.add({
              severity: 'error',
              summary: 'Sort not saved',
              detail: err.error?.message || 'Failed to update sort order for this location.',
            });
            return of(null);
          })
        )
        .subscribe(res => {
          if (!res || res.status === false) {
            if (res?.status === false) {
              this.messageService.add({
                severity: 'error',
                summary: 'Sort not saved',
                detail: res.message || 'The server did not accept this sort order.',
              });
            }
            return;
          }
          this.packageService.clearCarWashPackagesCache();
          if (this.currentFilters) {
            this.packageService.loadPackages(this.buildFilterParams(this.currentFilters));
          } else {
            this.packageService.loadPackages();
          }
          this.applyClientSideFilters();
          this.closeConfigureFilterModal();
          this.messageService.add({
            severity: 'success',
            summary: 'Sort updated',
            detail: 'Sales panel sort order was saved for this location.',
          });
        });
    };

    const locationId = this.resolveConfigureSalesLocationId();
    if (!locationId) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Location required',
        detail: 'Select a location first.',
      });
      return;
    }
    run(locationId);
  }

  // Check if any filters or search are currently active
  get hasActiveFiltersOrSearch(): boolean {
    return !!(this.currentSearchTerm || this.currentFilters);
  }

  /**
   * List table visibility — same idea as admin-orders: only render the table when
   * there are packages to show (hide for true empty and filter/search empty).
   */
  get showPackageTable(): boolean {
    return this.currentView === 'list' && this.packages.length > 0;
  }

  // Get empty state configuration based on whether filters are active
  get emptyStateConfig() {
    const config = (() => {
      // If no packages exist at all (original count is 0), show add button
      if (this.originalPackageCount === 0 && this.hasLoadedInitialPackages) {
        return {
          title: 'No packages added!',
          description: 'Create wash packages to offer services your customers can choose from.',
          showAddButton: true,
        };
      }
      // If packages exist originally but current view is empty (due to filters/search), don't show add button
      else if (this.originalPackageCount > 0 && this.packages.length === 0) {
        return {
          title: 'No result found',
          description:
            "Try adjusting your search terms or filters to find what you're looking for.",
          showAddButton: false,
        };
      }
      // Before first API response (handled in template with shimmer; avoid "no packages" flash)
      else if (!this.hasLoadedInitialPackages && this.allPackages.length === 0) {
        return {
          title: '',
          description: '',
          showAddButton: false,
        };
      }
      // Fallback case
      else {
        return {
          title: 'No packages found',
          description: '',
          showAddButton: false,
        };
      }
    })();

    this.logger.debug(
      'Empty state config',
      {
        originalPackageCount: this.originalPackageCount,
        packagesLength: this.packages.length,
        hasLoadedInitialPackages: this.hasLoadedInitialPackages,
        hasActiveFiltersOrSearch: this.hasActiveFiltersOrSearch,
        config,
      },
      'PackageComponent'
    );

    return config;
  }

  ngOnInit(): void {
    this.loadFamilyPlanFeature();
    this.isTableFiltering = false; // Reset table filtering state on init
    this.locationService.clearShopLocation();
    // Load list immediately so `view-all-by-user` runs before child layout work (same tick as possible).
    this.packageService.loadPackages();

    this.packageService.packagesLoading$.pipe(takeUntil(this.destroy$)).subscribe(loading => {
      if (!loading) {
        this.hasLoadedInitialPackages = true;
      }
    });

    this.packageService.packages$
      .pipe(takeUntil(this.destroy$))
      .subscribe((packages: Package[]) => {
        this.logger.debug(
          'Packages loaded from API',
          { count: packages.length },
          'PackageComponent'
        );
        this.allPackages = [...packages];

        if (!this.currentFilters && !this.isTableFiltering) {
          this.originalPackageCount = packages.length;
        }

        this.applyClientSideFilters();
      });

    // Track package view in Amplitude
    this.trackPackageView();

    // Check for query parameter to open modal (defer while a lane dialog holds the mutex)
    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe(params => {
      if (params['openModal'] === 'true') {
        const openPackageModal = () => this.openModal();
        if (this.dialogMutex.isLaneDialogOpen()) {
          this.logger.debug(
            'Deferring package openModal query until lane dialog releases the mutex'
          );
          this.dialogMutex.defer('feature-modal', openPackageModal);
        } else {
          openPackageModal();
        }
        // Remove the query parameter after handling
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: {},
          queryParamsHandling: 'merge',
        });
      }
    });
  }

  /**
   * Track package view in Amplitude (only once per page load)
   */
  trackPackageView(): void {
    // Only track once per page load
    if (this.hasTrackedPackageView) {
      return;
    }

    const user = this.authService.user();
    const shopId = user?.shop_id || user?.id || 'any';

    this.amplitudeService.trackEvent(
      'Services Viewed',
      {
        shop_id: shopId,
        view_type: 'package_list',
        timestamp: new Date().toISOString(),
      },
      user?.email || 'any'
    );

    // Mark as tracked to prevent multiple calls
    this.hasTrackedPackageView = true;
  }

  openModal(pkg: any = null) {
    this.selectedPackage = pkg;
    if (pkg) {
      this.showModal = true;
      return;
    }
    this.openPackageTypeModal();
  }

  openPackageTypeModal(): void {
    this.selectedPackageKind = 'package';
    this.showPackageTypeModal = true;
  }

  closePackageTypeModal(): void {
    this.showPackageTypeModal = false;
    this.selectedPackageKind = 'package';
  }

  onPackageTypeOverlayClick(event: MouseEvent): void {
    if (event.target === event.currentTarget) {
      this.closePackageTypeModal();
    }
  }

  onPackageTypeContinue(): void {
    if (this.selectedPackageKind === 'prepaid_plan') {
      this.closePackageTypeModal();
      this.openPrepaidPackageModal();
      return;
    }
    this.closePackageTypeModal();
    this.selectedPackage = null;
    this.showModal = true;
  }

  openPrepaidPackageModal(): void {
    this.prepaidEditBookId = null;
    this.prepaidEditData = null;
    this.showPrepaidModal = true;
  }

  openEditPrepaidModal(book: PrepaidWashBook): void {
    this.prepaidEditBookId = book.id;
    this.prepaidEditData = mapPrepaidWashBookToFormData(book);
    this.showPrepaidModal = true;
  }

  closePrepaidPackageModal(): void {
    this.showPrepaidModal = false;
    this.prepaidEditBookId = null;
    this.prepaidEditData = null;
  }

  onPrepaidSaved(_payload: PrepaidPackageFormData): void {
    // Create API, reload, and modal close are handled in add-prepaid-modal.
    this.prepaidWashBooksComponent?.refresh();
  }

  closeModal() {
    this.showModal = false;
    this.selectedPackage = null;
    // Reload packages with current filters if they exist
    if (this.currentFilters) {
      const apiParams = this.buildFilterParams(this.currentFilters);
      this.packageService.loadPackages(apiParams);
    } else {
      this.packageService.loadPackages();
    }
  }

  openDeleteModal(pkg: any) {
    this.packageToDelete = pkg;
    this.showDeleteModal = true;
  }

  closeDeleteModal() {
    this.showDeleteModal = false;
    this.packageToDelete = null;
  }

  deletePackage(pkg: any) {
    this.packageService.deletePackage(pkg.id).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Package deleted!',
          detail: '"' + pkg.package_name + '" package has been deleted successfully.',
        });
        this.closeDeleteModal();
        this.packageService.removePackageFromList(pkg.id);
      },
      error: () => {
        // Error handling is already done in the service
        this.closeDeleteModal();
      },
    });
  }

  duplicatePackage(pkg: any) {
    // Create a copy of the package with a new name and without ID
    const duplicatedPackage = {
      ...pkg,
      id: undefined, // Remove ID so it creates a new package
      package_name: `${pkg.package_name} (Copy)`, // Add "Copy" to the name
      created_at: undefined,
      updated_at: undefined,
      isDuplicate: true, // Flag to indicate this is a duplication
    };
    this.openModal(duplicatedPackage);
  }

  onViewChanged(view: string) {
    this.currentView = view;
  }

  onPackagePageTabChange(tabName: string): void {
    this.activePageTab = tabName;
  }

  onSearchChanged(searchTerm: string) {
    this.logger.debug('Search term changed', { searchTerm }, 'PackageComponent');
    this.currentSearchTerm = searchTerm;
    this.applyClientSideFilters();
  }

  onFiltersApplied(filters: FilterOptions) {
    this.logger.debug('Filters applied', filters, 'PackageComponent');
    this.currentFilters = filters;
    this.isTableFiltering = false; // Reset table filtering when main filters are applied
    this.syncShopLocationForPackageFilters(filters);

    // Use API-based filtering instead of client-side filtering
    // Build API parameters using the same logic as filter modal component
    const apiParams = this.buildFilterParams(filters);
    this.logger.debug('Loading packages with API filters', apiParams, 'PackageComponent');
    this.packageService.loadPackages(apiParams);
  }

  private syncShopLocationForPackageFilters(filters: FilterOptions): void {
    if (!filters.locations.length) {
      this.locationService.clearShopLocation();
    }
  }

  private applyClientSideFilters() {
    let filteredPackages = [...this.allPackages];

    // Apply search filter (always client-side)
    if (this.currentSearchTerm) {
      const searchLower = this.currentSearchTerm.toLowerCase();
      filteredPackages = filteredPackages.filter(
        pkg =>
          pkg.package_name?.toLowerCase().includes(searchLower) ||
          pkg.features?.some((feature: string) => feature.toLowerCase().includes(searchLower)) ||
          pkg.wash_types?.some((type: string) => type.toLowerCase().includes(searchLower)) ||
          pkg.locations_str?.toLowerCase().includes(searchLower)
      );
    }

    // Apply other filters client-side only if no API filters are active
    // (When API filters are applied, the packages from API are already filtered)
    if (this.currentFilters && !this.hasApiFilters(this.currentFilters)) {
      filteredPackages = this.applyFilters(filteredPackages, this.currentFilters);
    }

    this.packages = filteredPackages;
    this.logger.debug(
      'Filtered packages',
      {
        filteredCount: this.packages.length,
        // totalCount: this.allPackages.length,
        hasApiFilters: this.currentFilters ? this.hasApiFilters(this.currentFilters) : false,
        searchActive: !!this.currentSearchTerm,
      },
      'PackageComponent'
    );
  }

  private applyFilters(packages: Package[], filters: FilterOptions): Package[] {
    let filtered = [...packages];

    // Apply status filter
    if (filters.statuses.length > 0) {
      filtered = filtered.filter(pkg => {
        if (filters.statuses.includes('active') && filters.statuses.includes('inactive')) {
          return true; // Show both
        } else if (filters.statuses.includes('active')) {
          return pkg.package_status === true;
        } else if (filters.statuses.includes('inactive')) {
          return pkg.package_status === false;
        }
        return true;
      });
    }

    // Apply wash type filter
    if (filters.carWashTypes.length > 0) {
      filtered = filtered.filter(pkg => {
        return filters.carWashTypes.some(filterType => {
          let apiType = filterType;
          if (filterType === 'single-wash') apiType = 'single_wash';
          else if (filterType === 'monthly-membership') apiType = 'monthly_membership';
          else if (filterType === 'add-on') apiType = 'add_on';

          return (
            pkg.wash_types?.includes(apiType) || (filterType === 'add-on' && pkg.type === 'add_on')
          );
        });
      });
    }

    // Apply location filter
    if (filters.locations.length > 0) {
      filtered = filtered.filter(pkg => {
        return pkg.locations?.some(location => filters.locations.includes(location.id.toString()));
      });
    }

    return filtered;
  }

  buildFilterParams(filters: FilterOptions): any {
    // Note: sort_by is handled separately by triggering table column sorting
    return buildPackageFilterParams({
      statuses: filters.statuses,
      carWashTypes: filters.carWashTypes,
      locations: filters.locations,
    });
  }

  private hasApiFilters(filters: FilterOptions): boolean {
    return (
      filters.carWashTypes.length > 0 || filters.statuses.length > 0 || filters.locations.length > 0
    );
  }

  onTableSorted(sortOption: SortOption) {
    this.logger.debug('Table sort triggered', sortOption, 'PackageComponent');
    // Trigger the table component's sorting programmatically
    if (this.packageTableComponent && this.currentView === 'list') {
      // Use Material table's sorting approach
      const table = (this.packageTableComponent as any).dt;
      if (table) {
        table.sortField = sortOption.field;
        table.sortOrder = sortOption.order === 'asc' ? 1 : -1;
        table.sortSingle?.();

        // Update the component's tracking variables for icon display
        this.packageTableComponent.currentSortField = sortOption.field;
        this.packageTableComponent.currentSortOrder = sortOption.order;
      }
    }
  }

  // Method to notify main component when table is applying filters
  onTableFiltering(isFiltering: boolean) {
    this.isTableFiltering = isFiltering;
    this.logger.debug('Table filtering state changed', { isFiltering }, 'PackageComponent');
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
